import type { Dispatch, ReactElement, ReactNode } from "react"
import { useContext, useEffect } from "react"

import { useTranslation } from "react-i18next"
import type { Edge, Node } from "reactflow"
import { Background, ConnectionLineType, Controls, ReactFlow, useReactFlow } from "reactflow"

import MediationOutlinedIcon from "@mui/icons-material/MediationOutlined"
import { Button } from "@synapse-analytics/synapse-ui"

import { WorkflowCanvasContext } from "../../context/Context"
import { edgeTypes, nodeTypes } from "../../fixtures/workflow-fixtures"

import styles from "../../Workflows.module.scss"

type Props = {
  edges: Edge[]
  nodes: Node[]
  workflowControls?: ReactNode
  setIsSidebarOpen?: Dispatch<React.SetStateAction<boolean>>
}

/**
 * WorkflowCanvas component that renders the React Flow canvas for workflow visualization.
 * Provides zoom controls, background, and handles canvas positioning and interactions.
 */
export function WorkflowCanvas(props: Readonly<Props>): ReactElement {
  // Prop destructuring
  const { edges, nodes, workflowControls, setIsSidebarOpen } = props

  // Context hooks
  const { isWorkflowFetching, isWorkflowLoading, mode, onEdgesChange } = useContext(WorkflowCanvasContext)

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowCanvas",
  })

  // React Flow hooks
  const { fitView, zoomIn, zoomOut } = useReactFlow()

  // Constants
  const isCreateMode = mode === "create"
  const isEditMode = mode === "edit"

  // UseEffects
  /**
   * Adjusts canvas position (zoom, pan) when workflow canvas is mounted/re-loaded.
   * Automatically fits view for read mode after nodes are loaded.
   */
  useEffect(() => {
    if (!["create", "edit"].includes(mode)) {
      setTimeout(() => {
        fitView({ duration: 500 })
      }, 100)
    }
  }, [nodes.length, isWorkflowLoading, nodes, isWorkflowFetching, isCreateMode, isEditMode, fitView, mode])

  /* Reactflow canvas */
  return (
    <ReactFlow
      edgeTypes={edgeTypes}
      snapToGrid={true}
      nodes={isWorkflowLoading ? [] : nodes}
      nodeTypes={nodeTypes}
      fitView={isCreateMode}
      nodesDraggable={true}
      onEdgesChange={onEdgesChange}
      edges={edges}
      connectionLineType={ConnectionLineType.Bezier}
      deleteKeyCode={[]}
      className={`${styles.workflowCanvas} transition`}
    >
      <Background />
      <Controls
        onZoomIn={() => zoomIn({ duration: 400 })}
        onZoomOut={() => zoomOut({ duration: 400 })}
        onFitView={() => fitView({ duration: 500 })}
        position="top-left"
        showFitView={true}
        showInteractive={false}
        className={styles.zoomPanControls}
      >
        {setIsSidebarOpen && (
          <div style={{ marginBottom: "8px" }}>
            <Button
              variant="secondary"
              size="small"
              tooltip={t("workflowsTooltip")}
              onClick={() => setIsSidebarOpen((prev) => !prev)}
            >
              <MediationOutlinedIcon
                sx={{
                  width: "1rem",
                  height: "1rem",
                  py: "4px",
                }}
              />
            </Button>
          </div>
        )}
      </Controls>

      <Controls
        position="top-right"
        showFitView={false}
        showZoom={false}
        showInteractive={false}
        className={styles.removeShadow}
      >
        {workflowControls}
      </Controls>
    </ReactFlow>
  )
}
